import {
  emptySnapshot,
  type VocabularySnapshot,
} from "../../../domain/vocabulary/types.ts";
import { snapshotReplacingContent } from "../../../domain/vocabulary/mutations.ts";
import { vocabularySnapshotSchema } from "../../../contracts/storage/schemas.ts";
import {
  preMigrationCopySchema,
  type PreMigrationCopy,
} from "../../../contracts/storage/backup.ts";
import { productionRegistry } from "../../../contracts/storage/migrations/production.ts";
import { migrateStorageFile, utf8ByteSize } from "../../../contracts/storage/migrations/runner.ts";
import type { MigrationRegistry } from "../../../contracts/storage/migrations/registry.ts";
import type { MigrateResult } from "../../../contracts/storage/migrations/types.ts";
import { ProductError } from "../../../contracts/errors.ts";
import type {
  CommitResult,
  LoadResult,
  PreMigrationCopyInfo,
  ReplaceAllOptions,
  StorageStatus,
  VocabularyStore,
} from "../../../application/ports/storage.ts";

export const VOCABULARY_STORAGE_KEY = "vocably.vocabulary.v1";
export const VOCABULARY_PRE_MIGRATION_KEY = "vocably.vocabulary.pre-migration.v1";
export const LOCK_STORAGE_KEY = "vocably.vocabulary.lock";
export const VOCABULARY_LOCK_NAME = "vocably-vocabulary";

export type ExclusiveLockManager = {
  request<T>(
    name: string,
    options: { mode: "exclusive" },
    callback: () => Promise<T>,
  ): Promise<T>;
};

export type LocalStorageVocabularyStoreOptions = {
  now?: () => string;
  registry?: MigrationRegistry;
  locks?: ExclusiveLockManager | null;
};

type RawRead = { ok: true; value: string | null } | { ok: false };

function isQuotaError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const name = "name" in error ? String(error.name) : "";
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED";
}

function readKey(storage: Storage, key: string): RawRead {
  try {
    return { ok: true, value: storage.getItem(key) };
  } catch {
    return { ok: false };
  }
}

function navigatorLocks(): ExclusiveLockManager | null {
  if (typeof navigator === "undefined" || !("locks" in navigator) || !navigator.locks?.request) {
    return null;
  }
  return navigator.locks as unknown as ExclusiveLockManager;
}

function failureFromStatus(status: StorageStatus): CommitResult | null {
  switch (status.kind) {
    case "ready":
    case "empty":
      return null;
    case "unavailable":
      return { ok: false, code: "storage_unavailable", message: status.message };
    case "invalid":
      return { ok: false, code: "storage_corrupt", message: status.message };
    case "unsupported":
      return { ok: false, code: "unsupported_version", message: status.message };
    case "migration_failed":
      return { ok: false, code: "migration_failed", message: status.message };
    case "migration_blocked":
      return { ok: false, code: "read_only", message: status.message };
  }
}

export class LocalStorageVocabularyStore implements VocabularyStore {
  private snapshot: VocabularySnapshot | null = null;
  private raw: string | null = null;
  private status: StorageStatus = {
    kind: "unavailable",
    message: "Vocabulary has not been loaded yet.",
  };
  private writable = true;
  private readonly snapshotListeners = new Set<(snapshot: VocabularySnapshot) => void>();
  private readonly statusListeners = new Set<(status: StorageStatus) => void>();
  private readonly tabId = crypto.randomUUID();
  private lockTimer: number | undefined;
  private eventsBound = false;
  private readonly now: () => string;
  private readonly registry: MigrationRegistry;
  private readonly lockManager: ExclusiveLockManager | null | undefined;

  constructor(
    private readonly storage: Storage = window.localStorage,
    options: LocalStorageVocabularyStoreOptions = {},
  ) {
    this.now = options.now ?? (() => new Date().toISOString());
    this.registry = options.registry ?? productionRegistry;
    this.lockManager = options.locks;
  }

  async load(): Promise<LoadResult> {
    this.installLock();
    this.bindStorageEvents();
    return this.withLock(() => this.loadUnlocked());
  }

  async reload(): Promise<LoadResult> {
    return this.withLock(() => this.loadUnlocked());
  }

  getSnapshot(): VocabularySnapshot | null {
    return this.snapshot;
  }

  getStatus(): StorageStatus {
    return this.status;
  }

  subscribe(listener: (snapshot: VocabularySnapshot) => void): () => void {
    this.snapshotListeners.add(listener);
    return () => this.snapshotListeners.delete(listener);
  }

  subscribeStatus(listener: (status: StorageStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  isWritable(): boolean {
    return this.writable && (this.status.kind === "ready" || this.status.kind === "empty");
  }

  estimateBytes(snapshot: VocabularySnapshot): number {
    return utf8ByteSize(JSON.stringify(snapshot));
  }

  async exportRaw(): Promise<string | null> {
    if (this.raw != null) {
      return this.raw;
    }
    const read = readKey(this.storage, VOCABULARY_STORAGE_KEY);
    return read.ok ? read.value : null;
  }

  async exportPreMigrationCopy(): Promise<string | null> {
    const copy = this.readPreMigrationCopy();
    return copy?.originalBytes ?? null;
  }

  async removePreMigrationCopy(): Promise<CommitResult> {
    try {
      this.storage.removeItem(VOCABULARY_PRE_MIGRATION_KEY);
      this.setStatus({
        ...this.status,
        ...("preMigrationCopy" in this.status ? { preMigrationCopy: undefined } : {}),
      } as StorageStatus);
      if (this.snapshot && (this.status.kind === "ready" || this.status.kind === "empty")) {
        return { ok: true, snapshot: this.snapshot };
      }
      return { ok: true, snapshot: emptySnapshot() };
    } catch (error) {
      if (isQuotaError(error)) {
        return {
          ok: false,
          code: "quota_exceeded",
          message: "The pre-migration copy could not be removed because this device is out of storage space.",
        };
      }
      return {
        ok: false,
        code: "storage_unavailable",
        message: "The pre-migration copy could not be removed on this device.",
      };
    }
  }

  async replaceAll(snapshot: VocabularySnapshot, options: ReplaceAllOptions = {}): Promise<CommitResult> {
    return this.commit((current) => snapshotReplacingContent(current, snapshot), {
      expectedRevision: options.expectedRevision,
      origin: "replace",
    });
  }

  async commit(
    mutator: (current: VocabularySnapshot) => VocabularySnapshot,
    options: { expectedRevision?: number; origin?: "commit" | "replace" } = {},
  ): Promise<CommitResult> {
    return this.withLock(() => this.commitUnlocked(mutator, options));
  }

  private async loadUnlocked(): Promise<LoadResult> {
    const read = readKey(this.storage, VOCABULARY_STORAGE_KEY);
    if (!read.ok) {
      this.raw = null;
      this.snapshot = null;
      this.setStatus({
        kind: "unavailable",
        message: "Vocably could not read saved vocabulary on this device.",
      });
      return this.status;
    }
    this.raw = read.value;
    if (read.value == null) {
      this.applyEmpty();
      return this.status;
    }
    if (read.value.trim() === "") {
      this.snapshot = null;
      this.setStatus({
        kind: "invalid",
        raw: read.value,
        reason: "empty_bytes",
        message: "Saved vocabulary could not be read.",
      });
      return this.status;
    }
    return this.ingestPrimary(read.value, { persistMigration: true });
  }

  private async ingestPrimary(
    raw: string,
    options: { persistMigration: boolean },
  ): Promise<LoadResult> {
    const migrated = migrateStorageFile(raw, { migratedAt: this.now() }, this.registry);
    return this.applyMigrated(raw, migrated, options.persistMigration);
  }

  private applyMigrated(
    raw: string,
    migrated: MigrateResult,
    persistMigration: boolean,
  ): LoadResult {
    this.raw = raw;
    if (migrated.status === "malformed_json") {
      this.snapshot = null;
      this.setStatus({
        kind: "invalid",
        raw,
        reason: "malformed_json",
        message: "Saved vocabulary could not be read.",
      });
      return this.status;
    }
    if (migrated.status === "invalid_version" || migrated.status === "invalid_source" || migrated.status === "invalid_wrapper") {
      this.snapshot = null;
      this.setStatus({
        kind: "invalid",
        raw,
        reason: migrated.status === "invalid_wrapper" ? "invalid_wrapper" : migrated.status,
        message: "Saved vocabulary could not be read.",
        issues: "issues" in migrated ? migrated.issues : undefined,
      });
      return this.status;
    }
    if (migrated.status === "unsupported_future" || migrated.status === "unsupported_old") {
      this.snapshot = null;
      this.setStatus({
        kind: "unsupported",
        raw,
        version: migrated.version,
        direction: migrated.status === "unsupported_future" ? "future" : "old",
        message: migrated.message,
      });
      return this.status;
    }
    if (
      migrated.status === "conversion_failed" ||
      migrated.status === "invalid_intermediate" ||
      migrated.status === "invalid_target" ||
      migrated.status === "invalid_context"
    ) {
      this.snapshot = null;
      this.setStatus({
        kind: "migration_failed",
        raw,
        message: migrated.message,
        retryable: true,
        stage: "conversion",
        issues: "issues" in migrated ? migrated.issues : undefined,
        preMigrationCopy: this.preMigrationInfo(),
      });
      return this.status;
    }

    const snapshot = this.parseCurrent(migrated.snapshot);
    if (migrated.report.noop) {
      this.snapshot = snapshot;
      this.setStatus({
        kind: "ready",
        snapshot,
        origin: "load",
        lastReport: migrated.report,
        preMigrationCopy: this.preMigrationInfo(),
      });
      return this.status;
    }

    if (!persistMigration) {
      this.snapshot = null;
      this.setStatus({
        kind: "migration_failed",
        raw,
        message: "Saved vocabulary needs an upgrade before it can be used.",
        retryable: true,
        stage: "conversion",
        preMigrationCopy: this.preMigrationInfo(),
      });
      return this.status;
    }

    if (!this.hasExclusiveLocks()) {
      this.snapshot = null;
      this.setStatus({
        kind: "migration_blocked",
        raw,
        sourceVersion: migrated.report.sourceVersion,
        message:
          "This browser cannot safely upgrade saved vocabulary because it does not support Web Locks. Export a backup, then open Vocably in a current browser.",
      });
      return this.status;
    }

    return this.persistMigrated(raw, snapshot, migrated.report.sourceVersion, migrated.report);
  }

  private persistMigrated(
    originalRaw: string,
    snapshot: VocabularySnapshot,
    sourceVersion: number,
    report: NonNullable<Extract<StorageStatus, { kind: "ready" }>["lastReport"]>,
  ): LoadResult {
    const copy: PreMigrationCopy = {
      format: "vocably-pre-migration",
      formatVersion: 1,
      sourceVersion,
      targetVersion: report.targetVersion,
      migratedAt: report.migratedAt,
      originalBytes: originalRaw,
    };
    try {
      this.storage.setItem(VOCABULARY_PRE_MIGRATION_KEY, JSON.stringify(copy));
    } catch (error) {
      this.snapshot = null;
      this.raw = originalRaw;
      this.setStatus({
        kind: "migration_failed",
        raw: originalRaw,
        message: isQuotaError(error)
          ? "Vocably could not keep a pre-migration copy because this device is out of storage space. Export a backup, free space, then retry."
          : "Vocably could not keep a pre-migration copy, so the saved vocabulary was left unchanged.",
        retryable: true,
        stage: "recovery_copy",
        preMigrationCopy: this.preMigrationInfo(),
      });
      return this.status;
    }

    const serialized = JSON.stringify(snapshot);
    try {
      this.storage.setItem(VOCABULARY_STORAGE_KEY, serialized);
    } catch (error) {
      this.snapshot = null;
      this.raw = originalRaw;
      this.setStatus({
        kind: "migration_failed",
        raw: originalRaw,
        message: isQuotaError(error)
          ? "Vocably kept a pre-migration copy but could not save the upgraded vocabulary because this device is out of storage space."
          : "Vocably kept a pre-migration copy but could not save the upgraded vocabulary. The original data is still in place.",
        retryable: true,
        stage: "primary_write",
        preMigrationCopy: this.preMigrationInfo(),
      });
      return this.status;
    }

    this.raw = serialized;
    this.snapshot = snapshot;
    this.setStatus({
      kind: "ready",
      snapshot,
      origin: "migrate",
      lastReport: report,
      preMigrationCopy: this.preMigrationInfo(),
    });
    return this.status;
  }

  private async commitUnlocked(
    mutator: (current: VocabularySnapshot) => VocabularySnapshot,
    options: { expectedRevision?: number; origin?: "commit" | "replace" },
  ): Promise<CommitResult> {
    if (!this.writable) {
      return {
        ok: false,
        code: "read_only",
        message: "Another Vocably window is currently saving. This window is read-only.",
      };
    }

    const read = readKey(this.storage, VOCABULARY_STORAGE_KEY);
    if (!read.ok) {
      this.snapshot = null;
      this.raw = null;
      this.setStatus({
        kind: "unavailable",
        message: "Vocably could not read saved vocabulary on this device.",
      });
      return { ok: false, code: "storage_unavailable", message: this.status.kind === "unavailable" ? this.status.message : "Storage is unavailable." };
    }

    if (read.value != null && read.value.trim() !== "") {
      const ingested = await this.ingestPrimary(read.value, { persistMigration: true });
      if (!ingested || (ingested.kind !== "ready" && ingested.kind !== "empty")) {
        const failure = failureFromStatus(this.status);
        return failure ?? { ok: false, code: "storage_corrupt", message: "Saved vocabulary could not be read." };
      }
      if (ingested.kind === "ready" && ingested.origin === "migrate") {
        return {
          ok: false,
          code: "stale_revision",
          message: "Vocabulary was upgraded on this device. Review the latest data before saving.",
        };
      }
    } else if (read.value != null) {
      this.snapshot = null;
      this.raw = read.value;
      this.setStatus({
        kind: "invalid",
        raw: read.value,
        reason: "empty_bytes",
        message: "Saved vocabulary could not be read.",
      });
      return { ok: false, code: "storage_corrupt", message: "Saved vocabulary could not be read." };
    } else {
      this.applyEmpty();
    }

    if (options.expectedRevision != null && (this.snapshot?.dataRevision ?? 0) !== options.expectedRevision) {
      return {
        ok: false,
        code: "stale_revision",
        message: "Vocabulary changed while the backup confirmation was open. Export or review the latest data, then restore again.",
      };
    }

    const previousRaw = this.raw;
    const previousSnapshot = this.snapshot;
    const previousStatus = this.status;
    try {
      const next = this.parseCurrent(mutator(this.snapshot ?? emptySnapshot()));
      const serialized = JSON.stringify(next);
      this.storage.setItem(VOCABULARY_STORAGE_KEY, serialized);
      this.snapshot = next;
      this.raw = serialized;
      this.setStatus({
        kind: "ready",
        snapshot: next,
        origin: options.origin ?? "commit",
        preMigrationCopy: this.preMigrationInfo(),
      });
      return { ok: true, snapshot: next };
    } catch (error) {
      this.snapshot = previousSnapshot;
      this.raw = previousRaw;
      this.setStatus(previousStatus, { silent: true });
      if (error instanceof ProductError) {
        return { ok: false, code: error.code, message: error.message };
      }
      if (isQuotaError(error)) {
        return {
          ok: false,
          code: "quota_exceeded",
          message:
            "This device is out of storage space for Vocably. Export a backup or delete meanings, then retry.",
        };
      }
      return {
        ok: false,
        code: "storage_unavailable",
        message: "The vocabulary could not be saved on this device.",
      };
    }
  }

  private applyEmpty(): void {
    const snapshot = emptySnapshot();
    this.snapshot = snapshot;
    this.raw = null;
    this.setStatus({
      kind: "empty",
      snapshot,
      preMigrationCopy: this.preMigrationInfo(),
    });
  }

  private parseCurrent(value: unknown): VocabularySnapshot {
    if (this.registry === productionRegistry) {
      return vocabularySnapshotSchema.parse(value);
    }
    return this.registry.schemas.get(this.registry.currentVersion)!.parse(value) as VocabularySnapshot;
  }

  private preMigrationInfo(): PreMigrationCopyInfo | undefined {
    const copy = this.readPreMigrationCopy();
    if (!copy) {
      return undefined;
    }
    return {
      sourceVersion: copy.sourceVersion,
      targetVersion: copy.targetVersion,
      migratedAt: copy.migratedAt,
      byteSize: utf8ByteSize(copy.originalBytes),
    };
  }

  private readPreMigrationCopy(): PreMigrationCopy | null {
    const read = readKey(this.storage, VOCABULARY_PRE_MIGRATION_KEY);
    if (!read.ok || read.value == null) {
      return null;
    }
    try {
      return preMigrationCopySchema.parse(JSON.parse(read.value));
    } catch {
      return null;
    }
  }

  private setStatus(status: StorageStatus, options: { silent?: boolean } = {}): void {
    this.status = status;
    if (options.silent) {
      return;
    }
    if (status.kind === "ready" || status.kind === "empty") {
      for (const listener of this.snapshotListeners) {
        listener(status.snapshot);
      }
    }
    for (const listener of this.statusListeners) {
      listener(status);
    }
  }

  private hasExclusiveLocks(): boolean {
    return this.resolveLocks() != null;
  }

  private resolveLocks(): ExclusiveLockManager | null {
    if (this.lockManager === null) {
      return null;
    }
    if (this.lockManager) {
      return this.lockManager;
    }
    return navigatorLocks();
  }

  private async withLock<T>(run: () => Promise<T> | T): Promise<T> {
    const locks = this.resolveLocks();
    if (locks) {
      return locks.request(VOCABULARY_LOCK_NAME, { mode: "exclusive" }, async () => run());
    }
    return run();
  }

  private installLock(): void {
    if (this.hasExclusiveLocks()) {
      this.writable = true;
      return;
    }
    this.claimFallbackLock();
    if (this.lockTimer == null && typeof window !== "undefined") {
      this.lockTimer = window.setInterval(() => this.claimFallbackLock(), 1500);
    }
  }

  private claimFallbackLock(): void {
    try {
      const existing = this.storage.getItem(LOCK_STORAGE_KEY);
      const now = Date.now();
      if (existing) {
        const parsed = JSON.parse(existing) as { tabId: string; expiresAt: number };
        if (parsed.tabId !== this.tabId && parsed.expiresAt > now) {
          this.writable = false;
          return;
        }
      }
      this.storage.setItem(
        LOCK_STORAGE_KEY,
        JSON.stringify({ tabId: this.tabId, expiresAt: now + 4000 }),
      );
      this.writable = true;
    } catch {
      this.writable = false;
    }
  }

  private bindStorageEvents(): void {
    if (this.eventsBound || typeof window === "undefined") {
      return;
    }
    this.eventsBound = true;
    window.addEventListener("storage", (event) => {
      if (event.key !== null && event.key !== VOCABULARY_STORAGE_KEY) {
        return;
      }
      void this.withLock(async () => {
        await this.loadUnlocked();
      });
    });
  }
}
